// vim: set ts=2 sts=2 sw=2 et:

import React from "react";

import type { JudgeVoteValue } from "../judging/resolveJudgeVotes";
import { getLiftProgress, LiftProgressRow } from "../live/liftProgress";
import PlatformLiveAttemptHistory, { getLiftLabel } from "../live/PlatformLiveAttemptHistory";
import type { PlatformLiveAttempt } from "../live/platformLiveProtocol";
import { recordAttemptLabel, recordCategoryDataAttributes } from "../records/recordCategories";

import styles from "./SpectatorDisplay.module.scss";

interface Props {
  attempt: PlatformLiveAttempt;
  revealActive: boolean;
  revealAttemptKey: string | null;
  revealedResult: JudgeVoteValue | null;
}

const bestLabel = (row: LiftProgressRow): string => {
  if (row.state === "bombed") return "NULO";
  return row.bestKg === null ? "—" : `${row.bestKg} KG`;
};

const bestAriaLabel = (row: LiftProgressRow): string => {
  const lift = getLiftLabel(row.lift);
  const best = row.state === "bombed" ? "nulo" : row.bestKg === null ? "sin intentos" : `${row.bestKg} KG`;
  const record = row.recordCategory === undefined ? "" : `, ${recordAttemptLabel(row.recordCategory).toLocaleLowerCase("es")}`;
  return `${lift}: ${best}${record}`;
};

// El movimiento en curso se ve completo; los otros, solo con su mejor válido, en el orden de la competencia.
// Debajo, el total acumulado. Durante el veredicto el total ya suma el intento recién juzgado y no se atenúa.
const SpectatorLiftProgress = ({ attempt, revealActive, revealAttemptKey, revealedResult }: Props) => {
  const progress = getLiftProgress(attempt, revealActive ? { attemptKey: revealAttemptKey, result: revealedResult } : null);
  return (
    <div className={styles.liftProgress} aria-label="Progreso en el torneo">
      <div className={styles.liftStack} aria-label="Movimientos">
        {progress.rows.map((row) =>
          row.current ? (
            <PlatformLiveAttemptHistory
              key={row.lift}
              attempt={attempt}
              variant="spectator"
              embedded
              dimmed={revealActive}
              revealActive={revealActive}
              revealAttemptKey={revealAttemptKey}
              revealedResult={revealedResult}
            />
          ) : (
            <div
              key={row.lift}
              className={`${styles.collapsedLift} ${revealActive ? styles.collapsedLiftDimmed : ""}`}
              data-state={row.state}
              aria-label={bestAriaLabel(row)}
            >
              <span className={styles.collapsedLiftName}>{getLiftLabel(row.lift).toUpperCase()}</span>
              {row.recordCategory !== undefined ? (
                <span className={styles.collapsedLiftRecord} {...recordCategoryDataAttributes(row.recordCategory)}>
                  {recordAttemptLabel(row.recordCategory)}
                </span>
              ) : null}
              <strong className={styles.collapsedLiftBest}>{bestLabel(row)}</strong>
            </div>
          ),
        )}
      </div>
      {progress.showTotal ? (
        <div className={styles.liftTotal} data-bombed={progress.bombed ? "true" : undefined} aria-label="Total acumulado">
          <span>TOTAL ACUMULADO</span>
          <strong>{progress.totalKg === null ? "—" : `${progress.totalKg} KG`}</strong>
        </div>
      ) : null}
    </div>
  );
};

export default SpectatorLiftProgress;
